import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/cropUtils';
import './DocumentCropperModal.css';

interface DocumentCropperModalProps {
  image: string;
  documentLabel?: string;
  pageIndex?: number;
  onCropComplete: (croppedFile: File, continueAdding?: boolean) => void;
  onTriggerNextUpload?: () => void;
  onCancel: () => void;
}

const DocumentCropperModal: React.FC<DocumentCropperModalProps> = ({
  image,
  documentLabel = 'Belge',
  pageIndex = 1,
  onCropComplete,
  onTriggerNextUpload,
  onCancel
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [aspect, setAspect] = useState<number | undefined>(1 / 1.41); // Standart A4 dikey en-boy oranı
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = (c: { x: number; y: number }) => {
    setCrop(c);
  };

  const onZoomChange = (z: number) => {
    setZoom(z);
  };

  const onRotationChange = (r: number) => {
    setRotation(r);
  };

  const onCropCompleteInternal = useCallback((_croppedArea: any, pixels: any) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = async (continueAdding: boolean = false) => {
    if (!croppedAreaPixels) return;

    // iOS Safari Fix: Sonraki fotoğraf çekimi için input'u senkron tetikle
    if (continueAdding && onTriggerNextUpload) {
      onTriggerNextUpload();
    }

    try {
      setIsProcessing(true);
      const croppedBlob = await getCroppedImg(image, croppedAreaPixels, rotation);
      if (croppedBlob) {
        const file = new File(
          [croppedBlob],
          `belge_sayfa_${pageIndex}_${Date.now()}.jpg`,
          { type: 'image/jpeg' }
        );
        onCropComplete(file, continueAdding);
      }
    } catch (e) {
      console.error('Fotoğraf kırpma hatası:', e);
      alert('Görüntü işlenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsProcessing(false);
    }
  };

  const rotateLeft = () => setRotation((prev) => (prev - 90) % 360);
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360);

  const toggleAspect = (newAspect: number | undefined) => {
    setAspect(newAspect);
  };

  return (
    <div className="cropper-modal-overlay">
      <div className="cropper-modal-container premium-version">
        {/* Başlık Alanı */}
        <div className="cropper-header">
          <h3>
            ✂️ {documentLabel} Düzenle
            <span className="cropper-page-badge">{pageIndex}. Sayfa</span>
          </h3>
          <p className="cropper-subtitle">
            Çerçeveyi ayarlayın veya yakınlaştırarak belgeyi hizalayın
          </p>
          <button
            type="button"
            className="close-top-btn"
            onClick={onCancel}
            disabled={isProcessing}
            title="Kapat"
          >
            &times;
          </button>
        </div>

        {/* Görsel Kırpma Alanı */}
        <div className="cropper-content-wrapper">
          <div className="cropper-container">
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={onCropChange}
              onCropComplete={onCropCompleteInternal}
              onZoomChange={onZoomChange}
              onRotationChange={onRotationChange}
              showGrid={true}
              classes={{
                containerClassName: 'cropper-main-container',
                cropAreaClassName: 'cropper-area'
              }}
            />
          </div>
        </div>

        {/* Kontrol Paneli */}
        <div className="cropper-controls-panel">
          <div className="control-group buttons-row">
            <div className="aspect-toggles">
              <button
                type="button"
                className={`aspect-btn ${aspect === 1 / 1.41 ? 'active' : ''}`}
                onClick={() => toggleAspect(1 / 1.41)}
              >
                A4 (Standart)
              </button>
              <button
                type="button"
                className={`aspect-btn ${aspect === undefined ? 'active' : ''}`}
                onClick={() => toggleAspect(undefined)}
              >
                Serbest
              </button>
            </div>

            <div className="rotation-controls">
              <button
                type="button"
                className="rotate-btn"
                onClick={rotateLeft}
                title="Sola Döndür (90°)"
              >
                ↺
              </button>
              <button
                type="button"
                className="rotate-btn"
                onClick={rotateRight}
                title="Sağa Döndür (90°)"
              >
                ↻
              </button>
            </div>
          </div>

          <div className="control-group" style={{ marginTop: '8px' }}>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(e) => onZoomChange(Number(e.target.value))}
              className="zoom-range"
              title="Yakınlaştır / Uzaklaştır"
            />
          </div>
        </div>

        {/* Alt Butonlar */}
        <div className="cropper-footer">
          <button
            type="button"
            className="btn-cropper-cancel"
            onClick={onCancel}
            disabled={isProcessing}
          >
            İptal
          </button>

          <button
            type="button"
            className="btn-cropper-continue"
            onClick={() => handleSave(true)}
            disabled={isProcessing}
          >
            {isProcessing ? 'İşleniyor...' : '➕ Kırp ve Eklemeye Devam Et'}
          </button>

          <button
            type="button"
            className="btn-cropper-save"
            onClick={() => handleSave(false)}
            disabled={isProcessing}
          >
            {isProcessing
              ? 'Kaydediliyor...'
              : `✓ Kırp ve Kaydet ${pageIndex > 1 ? `(${pageIndex} Sayfa)` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentCropperModal;
